// client/src/components/ClassForm.jsx
// Create / edit form for a SchoolClass (wave-10).
//
// The class name is composed from a grade-letter picker (א–יב) + an
// optional number, joined with a dash → "א" / "ב-1". When editing a
// backfilled name that doesn't fit that shape (migrated from old free-text
// student classNames), the form falls back to a plain text input so the
// manager can still edit teacher/notes without the picker silently renaming
// the class.

import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '../lib/api.js';
import { ExclamationTriangleIcon } from './Icons.jsx';

// Grade letters, kindergarten through 12th, in aleph-bet order.
// KEEP IN SYNC with the server-side port in src/services/promote.js — the
// year-rollover grade bump must parse names exactly like this picker does.
export const GRADES = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ז', 'ח', 'ט', 'י', 'יא', 'יב'];

/** Parse "כיתה א" / "כיתה ב-1" (also accepts the old prefix-less "א" / "ב-1")
 *  → { grade, suffix }; null if it doesn't fit the shape. */
export function parseClassName(name) {
  if (!name) return null;
  const m = name.match(/^(?:כיתה\s+)?(יב|יא|[א-ת])(?:-(\d{1,3}))?$/);
  if (!m || !GRADES.includes(m[1])) return null;
  return { grade: m[1], suffix: m[2] || '' };
}

function composeName(grade, suffix) {
  const s = suffix.trim();
  return s ? `כיתה ${grade}-${s}` : `כיתה ${grade}`;
}

export default function ClassForm({ initial, onSubmit, onCancel, submitLabel, onDirtyChange }) {
  const parsed = useMemo(() => parseClassName(initial?.name), [initial]);
  // Free-text naming: a manager can call a class whatever they want ("כיתה א",
  // "קבוצה ירוקה", "שכבה ב'", …). The grade picker was too restrictive; the
  // *concept* is still a class, but the label is free.
  const pickerMode = false;

  const [grade, setGrade] = useState(parsed?.grade || 'א');
  const [suffix, setSuffix] = useState(parsed?.suffix || '');
  const [rawName, setRawName] = useState(initial?.name || '');
  const [teacher, setTeacher] = useState(initial?.teacher || '');
  const [notes, setNotes] = useState(initial?.notes || '');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [topError, setTopError] = useState(null);
  const topErrorRef = useRef(null);

  const name = pickerMode ? composeName(grade, suffix) : rawName.trim();

  const initialFlat = useMemo(
    () =>
      JSON.stringify({
        name: initial?.name || '',
        teacher: initial?.teacher || '',
        notes: initial?.notes || '',
      }),
    [initial]
  );
  const currentFlat = JSON.stringify({ name, teacher, notes });
  const isDirty = currentFlat !== initialFlat;

  useEffect(() => {
    onDirtyChange?.(isDirty);
    return () => onDirtyChange?.(false);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (topError && topErrorRef.current) {
      topErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [topError]);

  function handleCancel() {
    if (isDirty && !window.confirm('יש שינויים שלא נשמרו.\nלבטל?')) return;
    onCancel();
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setErrors({});
    setTopError(null);
    if (!name) {
      setErrors({ name: 'יש לבחור כיתה' });
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        name,
        teacher: teacher.trim() || null,
        notes: notes.trim() || null,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'conflict' && err.message === 'class_name_taken') {
          setErrors({ name: 'כיתה בשם הזה כבר קיימת' });
          setTopError('השם כבר תפוס בתכנית זו.');
        } else if (err.status === 422 && Array.isArray(err.details)) {
          const fieldErrors = {};
          for (const issue of err.details) fieldErrors[issue.path] = issue.message;
          setErrors(fieldErrors);
          setTopError('יש לתקן את השדות המסומנים ולנסות שוב.');
        } else if (err.status === 403) {
          setTopError('אין לך הרשאה לפעולה זו.');
        } else if (err.status >= 500) {
          setTopError(`שגיאת שרת (${err.status}). נסה שוב בעוד רגע.`);
        } else {
          setTopError(`שגיאה: ${err.message || err.code || 'לא צפויה'}`);
        }
      } else {
        setTopError('שגיאה לא צפויה. בדוק שיש חיבור אינטרנט ונסה שוב.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {topError && (
        <div
          ref={topErrorRef}
          role="alert"
          className="rounded-2xl bg-joy-50 border border-joy-200 text-joy-900 px-4 py-3 text-sm font-medium flex items-start gap-2 animate-slide-up"
        >
          <ExclamationTriangleIcon className="w-5 h-5 shrink-0 mt-0.5" />
          <span>{topError}</span>
        </div>
      )}

      {pickerMode ? (
        <Field label="כיתה" error={errors.name} required hint="בחר שכבה והוסף מספר אם צריך (לדוגמה כיתה א-1)">
          <div className="flex items-center gap-2">
            <select
              className="input flex-1"
              value={grade}
              onChange={(e) => setGrade(e.target.value)}
            >
              {GRADES.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
            <span className="text-ink-400 font-bold">-</span>
            <input
              type="text"
              inputMode="numeric"
              className="input w-24"
              value={suffix}
              onChange={(e) => setSuffix(e.target.value.replace(/\D/g, '').slice(0, 3))}
              aria-label="מספר כיתה (לא חובה)"
            />
            <span className="badge-indigo text-base shrink-0">{name || '—'}</span>
          </div>
        </Field>
      ) : (
        <Field label="שם הכיתה" error={errors.name} required hint="אפשר לקרוא לזה איך שרוצים — כיתה א, קבוצה ירוקה, שכבה ב׳…">
          <input
            type="text"
            className="input"
            value={rawName}
            onChange={(e) => setRawName(e.target.value)}
            maxLength={20}
            autoFocus
          />
        </Field>
      )}

      <Field label="מורה / מחנך/ת" error={errors.teacher} hint="שם המורה האחראי/ת על הכיתה">
        <input
          type="text"
          className="input"
          value={teacher}
          onChange={(e) => setTeacher(e.target.value)}
          maxLength={80}
        />
      </Field>

      <Field label="הערות" error={errors.notes}>
        <textarea
          className="input"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={500}
        />
      </Field>

      <div className="flex justify-end gap-2 pt-2">
        <button type="button" className="btn-secondary" onClick={handleCancel}>
          ביטול
        </button>
        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting ? 'שומר...' : submitLabel || 'שמור'}
        </button>
      </div>
    </form>
  );
}

function Field({ label, error, hint, required, children }) {
  return (
    <label className="block">
      <span className="label">
        {label} {required && <span className="text-joy-600">*</span>}
      </span>
      {children}
      {hint && !error && <span className="hint">{hint}</span>}
      {error && <span className="field-error">{error}</span>}
    </label>
  );
}
